import { useCallback, useMemo, useState } from 'react';

import {
  PROOF_SEQUENCE,
  ProofState,
  advance,
  createProof,
  isProven,
  outputAvailable,
  proofBadge,
} from '@cortis/proof-lifecycle';

/**
 * Proof panel, §3.4 and §10.11/§10.12.
 *
 * The only vertical UI slice in this repo so far, and it is the one that
 * matters most to get right: this component is where the product either tells
 * the truth about chain state or quietly lies. Three constraints shape it.
 *
 *   - It renders from the shared state machine in @cortis/proof-lifecycle. It
 *     computes no lifecycle logic of its own, so the badge it shows and the
 *     badge the worker persists cannot diverge.
 *
 *   - It never advances to confirmed on its own. `onRetry` and the props are
 *     the only inputs; there is no timer, no optimistic hop, and no local
 *     "probably fine by now" heuristic.
 *
 *   - The deliverable renders as soon as it exists, with the pending label
 *     attached. Withholding finished work until finality would make the
 *     product feel broken and buys no honesty that the label does not.
 *
 * Styling is via token custom properties only. No hex literal appears below;
 * every colour is a token whose contrast is asserted in @cortis/design-tokens.
 */

const STEP_DETAIL = {
  [ProofState.QUEUED]: 'Waiting for an execution slot',
  [ProofState.EXECUTING]: 'The agent is working through its plan',
  [ProofState.EVIDENCE_FINALIZING]: 'Hashing the evidence chain and building the run manifest',
  [ProofState.ATTESTATION_SIGNING]: 'The runtime attestor is signing the certificate',
  [ProofState.TRANSACTION_SUBMITTED]: 'Broadcast to opBNB',
  [ProofState.CONFIRMING]: 'Waiting for confirmations and for the indexer to catch up',
  [ProofState.CONFIRMED_AND_INDEXED]: 'Attested on chain and visible in the public record',
};

/**
 * @param {object} props
 * @param {import('@cortis/proof-lifecycle').ProofSnapshot} props.proof
 * @param {{title: string, body: string} | null} props.deliverable
 * @param {(runId: string) => void} props.onRetry
 * @param {number} [props.requiredConfirmations]
 */
export function ProofPanel({ proof, deliverable, onRetry, requiredConfirmations = 3 }) {
  const badge = proofBadge(proof.state);
  const currentIndex = PROOF_SEQUENCE.indexOf(proof.state);
  const failed = proof.state === ProofState.PROOF_FAILED;
  const showOutput = outputAvailable(proof.state) && deliverable !== null;

  return (
    <section className="proof-panel" aria-labelledby="proof-heading">
      <header className="proof-panel__header">
        <div>
          <h2 id="proof-heading" className="proof-panel__title">
            Run proof
          </h2>
          <p className="proof-panel__run">
            <span className="mono">{proof.runId}</span>
          </p>
        </div>
        <ProofBadge tone={badge.tone} label={badge.label} />
      </header>

      {showOutput && (
        <article className="proof-panel__output">
          <h3 className="proof-panel__output-title">{deliverable.title}</h3>
          <p className="proof-panel__output-body">{deliverable.body}</p>
          {!isProven(proof.state) && (
            /*
             * The output is real and finished. The proof is not. Saying so
             * inline, next to the work itself, is what stops "delivered" from
             * being read as "attested".
             */
            <p className="proof-panel__output-note">
              This result is ready to use. Its on-chain proof is still being confirmed.
            </p>
          )}
        </article>
      )}

      <ol className="proof-timeline" aria-label="Proof lifecycle">
        {PROOF_SEQUENCE.map((step, i) => {
          const state = failed
            ? i < currentIndexOrLast(proof, i)
              ? 'done'
              : 'blocked'
            : i < currentIndex
              ? 'done'
              : i === currentIndex
                ? 'active'
                : 'upcoming';
          return (
            <li key={step} className={`proof-step proof-step--${state}`} aria-current={state === 'active' ? 'step' : undefined}>
              <span className="proof-step__marker" aria-hidden="true" />
              <div className="proof-step__text">
                <span className="proof-step__label">{step}</span>
                <span className="proof-step__detail">{STEP_DETAIL[step]}</span>
              </div>
            </li>
          );
        })}
      </ol>

      <dl className="proof-facts">
        <Fact label="Run ID hash" value={proof.runIdHash} mono truncate />
        <Fact label="Transaction" value={proof.txHash ?? 'Not yet submitted'} mono truncate={Boolean(proof.txHash)} />
        <Fact
          label="Confirmations"
          value={proof.txHash ? `${proof.confirmations} of ${requiredConfirmations}` : '—'}
        />
        <Fact label="Indexed" value={proof.indexed ? 'Yes' : 'No'} />
        {proof.retries > 0 && <Fact label="Retries" value={String(proof.retries)} />}
      </dl>

      {failed && (
        <div className="proof-panel__failure" role="alert">
          <p className="proof-panel__failure-text">
            The proof could not be submitted. The run and its evidence are intact, and retrying reuses the same run
            identity rather than creating a second attestation.
          </p>
          {proof.lastError && <p className="proof-panel__failure-detail mono">{proof.lastError}</p>}
          <button type="button" className="button button--primary" onClick={() => onRetry(proof.runId)}>
            Retry proof
          </button>
        </div>
      )}
    </section>
  );
}

function currentIndexOrLast(proof, i) {
  // On failure the sequence position is wherever it stalled; everything before
  // it genuinely happened and should still read as done.
  const submitted = proof.txHash ? PROOF_SEQUENCE.indexOf(ProofState.TRANSACTION_SUBMITTED) + 1 : PROOF_SEQUENCE.indexOf(ProofState.ATTESTATION_SIGNING);
  return submitted;
}

function ProofBadge({ tone, label }) {
  return (
    <span className={`badge badge--${tone}`} role="status">
      {tone === 'pending' && <span className="badge__pulse" aria-hidden="true" />}
      {label}
    </span>
  );
}

function Fact({ label, value, mono = false, truncate = false }) {
  return (
    <div className="proof-facts__row">
      <dt className="proof-facts__label">{label}</dt>
      <dd className={`proof-facts__value${mono ? ' mono' : ''}${truncate ? ' proof-facts__value--truncate' : ''}`}>
        {truncate ? <span title={value}>{shorten(value)}</span> : value}
      </dd>
    </div>
  );
}

function shorten(v) {
  return typeof v === 'string' && v.length > 20 ? `${v.slice(0, 10)}…${v.slice(-8)}` : v;
}

/**
 * Demonstration harness for the slice. Drives the real state machine from
 * explicit inputs so the whole lifecycle, including failure and retry, can be
 * exercised without a chain.
 *
 * This is a development surface, not a product screen: §1.8 forbids fake
 * transaction confirmations in production flows, and the confirmation here is
 * driven by an operator clicking rather than by a timer pretending to be a
 * chain.
 */
export function ProofPanelHarness() {
  const [proof, setProof] = useState(() =>
    createProof({ runId: 'run_01JCORTIS0000000000000009', runIdHash: `0x${'ab'.repeat(32)}` }),
  );

  const step = useCallback((next, evidence) => {
    setProof((p) => {
      try {
        return advance(p, next, evidence);
      } catch {
        return p;
      }
    });
  }, []);

  const deliverable = useMemo(
    () => ({
      title: 'Open commitments before the board meeting',
      body: 'Three items are outstanding: the platform and security hiring plan, the observability renewal, and one deal that slipped to Q4 on procurement.',
    }),
    [],
  );

  return (
    <div className="harness">
      <ProofPanel proof={proof} deliverable={deliverable} onRetry={() => step(ProofState.ATTESTATION_SIGNING)} />
      <div className="harness__controls">
        <button type="button" className="button button--ghost" onClick={() => step(ProofState.EXECUTING)}>Execute</button>
        <button type="button" className="button button--ghost" onClick={() => step(ProofState.EVIDENCE_FINALIZING)}>Finalize evidence</button>
        <button type="button" className="button button--ghost" onClick={() => step(ProofState.ATTESTATION_SIGNING)}>Sign</button>
        <button type="button" className="button button--ghost" onClick={() => step(ProofState.TRANSACTION_SUBMITTED, { txHash: `0x${'9f'.repeat(32)}` })}>Submit</button>
        <button type="button" className="button button--ghost" onClick={() => step(ProofState.CONFIRMING, { confirmations: 1 })}>Confirming</button>
        <button type="button" className="button button--primary" onClick={() => step(ProofState.CONFIRMED_AND_INDEXED, { confirmations: 3, indexed: true })}>Confirm + index</button>
        <button type="button" className="button button--danger" onClick={() => step(ProofState.PROOF_FAILED, { error: 'AllEndpointsFailed: 2 RPC endpoints failed' })}>Fail</button>
      </div>
    </div>
  );
}
